/**
 * General "Password required" on-connect prompt.
 *
 * Shown when the user connects a saved connection whose auth needs a password
 * and none is stored (e.g. connections imported from Azure Data Studio, or the
 * long-standing "saved connection without a password" papercut). Mounted once
 * globally in App; driven entirely by connectPasswordPromptStore.
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Checkbox } from '../../../components/ui/checkbox';
import { useConnectPasswordPromptStore } from '../store/connectPasswordPromptStore';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { safeConnectionName } from '../../../types/safeConnectionDisplay';

export function ConnectPasswordPromptDialog() {
  const { track } = useAnalytics();
  const isOpen = useConnectPasswordPromptStore((s) => s.isOpen);
  const connection = useConnectPasswordPromptStore((s) => s.connection);
  const submit = useConnectPasswordPromptStore((s) => s.submit);
  const cancel = useConnectPasswordPromptStore((s) => s.cancel);

  const [password, setPassword] = useState('');
  const [save, setSave] = useState(true);

  // Reset the field each time a new prompt opens.
  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setSave(true);
    }
  }, [isOpen, connection?.id]);

  if (!connection) return null;

  const subtitle = `${connection.login || 'user'} @ ${safeConnectionName(connection)}`;

  const handleSubmit = () => {
    track('feature:connect_password_prompt', { saved: save });
    submit({ password, save });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && cancel()}>
      <DialogContent className="sm:max-w-sm" data-testid="connect-password-prompt">
        <DialogHeader>
          <DialogTitle>Password required</DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground -mt-1 break-all">{subtitle}</p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          className="space-y-3"
        >
          <Input
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            data-testid="connect-password-input"
            aria-label="Password"
          />

          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <Checkbox
              checked={save}
              onCheckedChange={(checked) => setSave(checked === true)}
              data-testid="connect-password-save"
            />
            Save password for this connection
          </label>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="ghost" onClick={cancel}>
              Cancel
            </Button>
            <Button type="submit" data-testid="connect-password-submit">
              Connect
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
